import {
  Component,
  Input,
  OnInit,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { ApiService } from '@/app/services/shared/api.service';
import { TemplateRef, ViewChild } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { NgToastService } from 'ng-angular-popup';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
export enum USER_ROLE_MESSAGES {
  REQUIRED_FIELDS_MISSING = 'Fill all the require fields',
  ADD_ROLE = 'Role added successfully!',
  UPDATE_ROLE = 'Role updated successfully!',
  DELETE_ROLE = 'Role deleted successfully!',
}
@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    DragDropModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatGridListModule,
    FormsModule,
    MatCheckboxModule,
  ],
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.css'],
})
export class RolesComponent implements OnInit, OnChanges {
  @Input() isDialogOpen!: boolean;
  @Input() selectedTab!: string;
  @ViewChild('addRoleDialog') addRoleDialog!: TemplateRef<any>;
  @ViewChild('viewDetailDialog') viewDetailDialog!: TemplateRef<any>;

  org_info: any = localStorage.getItem('org_info')
    ? JSON.parse(localStorage.getItem('org_info') as string)
    : null;
  roles: any[] = [];
  permissions: any[] = [];
  editRoleId: number;
  availableRoles: string[] = [];
  roleDetail: any;
  roleForm = new FormGroup({
    roleName: new FormControl('', Validators.required),
    scope: new FormControl('organization', Validators.required),
  });
  selectedPermissions: number[] = [];
  constructor(
    private apiService: ApiService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private toast: NgToastService
  ) {}

  ngOnInit(): void {
    this.loadRoles();
    this.loadPermissions();
  }
  loadRoles() {
    this.apiService.getRoles().subscribe((data) => {
      this.roles = data;
      this.availableRoles = this.roles.map((r: any) => r.name);
    });
  }
  loadPermissions() {
    this.apiService.getPermissions().subscribe((data) => {
      this.permissions = data;
    });
  }
  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['isDialogOpen'] &&
      !changes['isDialogOpen'].firstChange &&
      this.selectedTab === 'roles'
    ) {
      this.editRoleId = null;
      this.roleForm.reset();
      this.loadPermissions();
      this.openAddRoleDialog();
    }
  }
  togglePermission(permissionId: number) {
    const index = this.selectedPermissions.indexOf(permissionId);
    if (index === -1) {
      this.selectedPermissions.push(permissionId);
    } else {
      this.selectedPermissions.splice(index, 1);
    }
  }
  closeDialog() {
    this.dialog.closeAll();
  }
  openAddRoleDialog() {
    this.dialog.open(this.addRoleDialog, {
      disableClose: true,
      autoFocus: false,
    });
  }
  // Handle role form
  async createRole() {
    const dataToSubmit: any = await this.prepareRoleDataToSubmit();
    if (this.editRoleId) {
      this.apiService.updateRole(dataToSubmit, this.editRoleId).subscribe({
        next: (response: any) => {
          if (response.meta.success) {
            this.toast.success(USER_ROLE_MESSAGES.UPDATE_ROLE, 'SUCCESS', 4000);
            this.roleForm.reset();
            this.loadRoles();
            this.closeDialog();
          }
        },
        error: (error: any) => {
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    } else {
      this.apiService.createRole(dataToSubmit).subscribe({
        next: (response: any) => {
          if (response.meta.success) {
            this.toast.success(USER_ROLE_MESSAGES.ADD_ROLE, 'SUCCESS', 4000);
            this.roleForm.reset();
            this.loadRoles();
            this.closeDialog();
          }
        },
        error: (error: any) => {
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    }
  }

  async prepareRoleDataToSubmit() {
    if (this.roleForm.invalid) {
      this.toast.warning(
        USER_ROLE_MESSAGES.REQUIRED_FIELDS_MISSING,
        'WARNING',
        4000
      );
      this.roleForm.markAllAsTouched();
      return;
    }
    const dataToSubmit = {
      organization_id: this.org_info?.id,
      name: this.roleForm.value.roleName,
      scope: this.roleForm.value.scope,
      permission_ids: this.selectedPermissions,
    };
    return dataToSubmit;
  }
  getPermissionNames(permissions: any[]): string {
    return (
      permissions?.map((p) => p.name).join(', ') || 'No permissions assigned'
    );
  }
  deleteRole(roleId: number) {
    this.apiService.deleteRole(roleId).subscribe({
      next: (response: any) => {
        if (response.meta.success) {
          this.toast.success(USER_ROLE_MESSAGES.DELETE_ROLE, 'SUCCESS', 4000);
          this.loadRoles();
        }
      },
      error: (error: any) => {
        this.toast.danger(error.error.message, 'DANGER', 4000);
      },
    });
  }
  editRole(role: any) {
    // Patch values into the form
    this.roleForm.patchValue({
      roleName: role?.name,
      scope: role?.scope,
    });
    this.selectedPermissions = role?.permissions?.map(
      (permission) => permission?.id
    );
    this.editRoleId = role?.id;
    this.dialog.open(this.addRoleDialog);
  }
  resetPermissions() {
    this.selectedPermissions = [];
  }
  openViewDetailDialog(role: any) {
    this.roleDetail = role;
    this.dialog.open(this.viewDetailDialog, {
      disableClose: true,
      autoFocus: false,
    });
  }
}
